import React, { useCallback, useEffect, useRef, useState, useContext, useMemo } from 'react';
import {
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  IDetailsRowProps,
  Selection,
  SelectionMode,
} from '@fluentui/react/lib/DetailsList';
import type { IRenderFunction } from '@fluentui/react/lib/Utilities';
import { MarqueeSelection } from '@fluentui/react/lib/MarqueeSelection';
import { AcceptIcon, ProviderConsumer as FluentUIThemeConsumer } from '@fluentui/react-northstar';
import {
  ApprovalListIcon,
  TaskListIcon,
  TicketListIcon,
} from '../../../../svg';
import EmptySVG from './../../../../svg/empty.svg';
import { MyWorkListDataI } from '../MyWork';
import { ThemeColorScheme } from '../../../shared/common/TeamsTheme';
import { SkeletonTable } from '../../../shared/components/Skeleton';
import {
  SkeletonCell, hierarchialCategoryName, hierarchialPriorityName, hierarchialStatusName, hierarchialTicketType,
} from '../../../shared/components/table/TicketListBoard';
import { LinkRecord, SLAPills, StatusPillCell, _getUser, _getUsers } from '../../../shared/components/table/ClassDetailList';
import { getStatus } from '../../ticketHelper';
import { appState, AppStateValue } from '../../../AppState';
import { ConvertDateToLocale } from '../../../shared/common/ConvertDate';
import { TicketLifecyclePhaseElement } from '../../../shared/components/TicketLifecycleTab/TicketLifecyclePhaseRibbon';
import '../myWork.css'
import { EnumSortOrderType } from '../../toolbar/CustomViewContextProvider';
import { DEFAULT_SORT_ORDER_MY_WORK, MyWorkContext } from '../MyWorkContextProvider';
import useMyWorkListFunctions from './useMyWorkListFunctions';
import { getSelectedTabFromStorage, getMyWorkSortOrderFromStorage, myWorkColumnOrderKey, myWorkColumnWidthsKey, myWorkHiddenColumnsKey, setMyWorkSortOrderInStorage } from './myWorkListHelper';
import { COLUMN_DRAG_FLOOR, LIST_PREFERENCES_CHANGED, ListPreferencesChangedDetail, applyColumnOrder, applyColumnWidths, applyHiddenColumns, hideColumn, moveColumn, readColumnOrder, readColumnWidths, readHiddenColumns, saveColumnWidth, writeListPreference } from '../../../shared/components/table/listPreferences';
import { headerKeyedByColumnOrder, columnPointerDrag, markSortableColumns, seedAutoFitWidths } from '../../../shared/components/table/columnDrag';
import { ConfigurationName, CustomPropertiesAnswerJsonKey, LICENSE_FEATURE, ListColumnKey, MyWorkListViewType, PRODUCT_LICENSE } from '../../../shared/utils/constants';
import { Route } from '../../../../route-constants';
import { useLocation } from 'react-router-dom';
import { EmptyData } from '../../../shared/components/EmptyData';
import { DateTime } from 'luxon';
import { i18n, openGroupChat } from '../../../shared/utils/helper';
import { AsyncCircleImage } from '../../../shared/components/CircleImages';
import { ICustomProperty } from '../../../shared/interfaces/customproperties.interface';
import { CustomPropertyResting } from '../../../shared/components/table/CustomPropertyResting';
import { getCachedFeature } from '../../../shared/cache/FeatureCache';
import { platformService } from '../../../shared/services/platform.service';
import {
  MyWorkEditContext, MyWorkEditContextValue, MyWorkTicketRow, TaskAssignmentCell, TaskStatusCell,
  TicketAssignmentCell, TicketCustomPropertyCell, TicketDateCell, TicketPickerCell, TicketRequesterCell, TicketStatusCell, TicketTagsCell
} from './MyWorkInlineCells';
import { AssignmentColumn } from '../../../shared/components/table/InlineAssignmentCell';
import { NO_SELECT_ON_FOCUS, cellEditingOn } from '../../../shared/components/table/inlineCell';
import { LeavingRows, leaveClass, rowLeaver } from '../../../shared/components/table/rowLeave';
import { EMPTY_CELL, hoverTitle } from '../../../shared/components/table/emptyCell';
import { MyWorkApproversCell } from './MyWorkApproversCell';
import { DurationText } from '../../../shared/components/TicketTimeTracking/DurationText';
import { withArchived } from '../../../shared/utils/archivedPickers';

const { t } = i18n();

export interface IDetailsListBasicExampleItem {
  key: number;
  name: string;
  value: number;
}

export interface IDetailsListBasicExampleState {
  items: IDetailsListBasicExampleItem[];
  selectionDetails: string;
}

const asRow = (item: MyWorkListDataI) => item as unknown as MyWorkTicketRow;

const myWorkDate = (iso: string | null | undefined) =>
  <span>{iso ? ConvertDateToLocale(getDateOrEmptyString(iso), DateTime.DATE_SHORT) : '-'}</span>;

/** The editor for a row's routing cell: a ticket's or a task's panel; approvals draw as they are. */
const routingCell = (item: MyWorkListDataI, resting: React.ReactElement, column: AssignmentColumn) => {
  if (item.ItemType === MyWorkListViewType.Tickets) return <TicketAssignmentCell row={asRow(item)} resting={resting} column={column} />;
  if (item.ItemType === MyWorkListViewType.Tasks) return <TaskAssignmentCell row={asRow(item)} resting={resting} column={column} />;
  return resting;
};

const textCell = (text: string) => (
  <div className={`w-full truncate mr-4`} dir='rtl' {...hoverTitle(text)}>
    <bdi>{text || '-'}</bdi>
  </div>
);

const renderRecord = (
  item: MyWorkListDataI
): { [x: string]: React.ReactElement } => {
return {
  Id: <LinkRecord {...{ticketId: item.TicketId, itemId: item.Id, value: item.TicketId, type: item.ItemType, title: item.TicketTitle }} />,
  Title: <LinkRecord {...{ticketId: item.TicketId, itemId: item.Id, value: item.Title, type: item.ItemType, title: item.Title }} />,
  Type: <TicketPickerCell row={asRow(item)} field='TicketTypeId' resting={textCell(item.TicketType)} />,
  Category: <TicketPickerCell row={asRow(item)} field='CategoryId' resting={textCell(item.Category)} />,
  Priority: <TicketPickerCell row={asRow(item)} field='PriorityId' resting={textCell(item.Priority)} />,
  StatusName: item.ItemType === MyWorkListViewType.Tickets
    ? <TicketStatusCell
        row={asRow(item)}
        resting={item.StatusRecord ? <StatusPillCell status={item.StatusRecord} path={item.StatusName} /> : <span>{item.StatusName}</span>}
        renderPill={statusId => statusId == null ? <span>{item.StatusName}</span> : (
          <StatusPillCell
            status={getStatus(statusId, withArchived(appStateForCells.current?.ticketStatus, appStateForCells.current?.archivedTicketStatus))}
            path={hierarchialStatusName({ statusId, ticketStatuses: withArchived(appStateForCells.current?.ticketStatus, appStateForCells.current?.archivedTicketStatus) })}
          />
        )}
      />
    : <TaskStatusCell row={asRow(item)} resting={<span>{item.StatusName}</span>} />,
  Team: routingCell(item, <span>{item.TeamName}</span>, 'team'),
  Group: routingCell(item, <span>{item.GroupName}</span>, 'group'),
  SLAs: item.TicketSlas?.length > 0 ? <SLAPills ticketSlas={item.TicketSlas} /> : <>-</>,
  Phase: item.LifecyclePhaseName ? (<TicketLifecyclePhaseElement status={item.LifeCycleStatus} phaseName={item.LifecyclePhaseName} titleWhenCut />) : (<span>-</span>),
  Lifecycle: textCell(item.LifecycleName),
  GroupChat:   <>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {item.Members && item.Members.length > 0 && (
          <AsyncCircleImage members={item.Members} />
        )}
        {item.GroupChatId ? (
          <a href="#" onClick={() => openGroupChat(item.ConversationId)}>
            {t('ticket.ticket-board.ticket-card.open-chat')}
          </a>
        ) : (
          <span>{t('ticket.ticket-board.ticket-card.no-chat')}</span>
        )}
      </div>
      </>,
  Requester: item.ItemType === MyWorkListViewType.Tickets ? <TicketRequesterCell row={asRow(item)} resting={requesterResting(item)} /> : requesterResting(item),
}};

const requesterResting = (item: MyWorkListDataI) => {
  if (item.Requester.length > 1) return _getUsers(item.Requester.map(r => ({ PlatformUserId: r.Id, PlatformUser: r })));
  if (item.Requester.length == 1) return _getUser(item.Requester[0]?.AadObjectId ? item.Requester[0]?.AadObjectId : item.Requester[0]?.UserName, item.Requester[0]?.FullName);
  return <>-</>;
};

const assigneeResting = (item: MyWorkListDataI) => {
  if (item.Assignee.length > 1) return _getUsers(item.Assignee.map(r => ({ PlatformUserId: r.Id, PlatformUser: r })), t('ticket.ticket-list.collaborators'));
  if (item.Assignee.length == 1) return _getUser(item.Assignee[0]?.AadObjectId ? item.Assignee[0]?.AadObjectId : item.Assignee[0]?.UserName, item.Assignee[0]?.FullName);
  return <>-</>;
};

const renderRecordRest = (item: MyWorkListDataI): { [x: string]: React.ReactElement } => ({
  // An approval row's Assignee column shows its approvers, which are what can be changed there.
  Assignee: (item.ItemType === MyWorkListViewType.Approvals
    ? (resting: React.ReactElement) => <MyWorkApproversCell row={item} resting={resting} />
    : (resting: React.ReactElement) => routingCell(item, resting, 'assignee'))(assigneeResting(item)),
  Tags: <TicketTagsCell row={asRow(item)} />,
  // A task or approval shows its ticket's template.
  Template: (
    <div className='w-full truncate mr-4' title={item.TemplateName || undefined}>
      <bdi>{item.TemplateName || (item.TemplateId ? t('ticket.child-filter.no-access') : '-')}</bdi>
    </div>
  ),
  // A ticket's own: a task or an approval has none, though the server sends its ticket's (Shaun, 2026-10-23).
  TotalAgentTime: item.ItemType === MyWorkListViewType.Tickets
    ? <span><DurationText minutes={item.TotalTimeSpentMinutes ?? 0} plain /></span>
    : <span>{EMPTY_CELL}</span>,
  Source: (
    <div className='w-full truncate mr-4' {...hoverTitle(item.ItemType === MyWorkListViewType.Tickets ? item.SourceName : undefined)}>
      <bdi>{(item.ItemType === MyWorkListViewType.Tickets && item.SourceName) || EMPTY_CELL}</bdi>
    </div>
  ),
  'Last Modified Date': (<span>{ConvertDateToLocale(item.LastModified, DateTime.DATE_SHORT)}</span>),
  'Created Date': <span>{ConvertDateToLocale(item.CreatedDate, DateTime.DATE_SHORT)}</span>,
  'Due Date': <TicketDateCell row={asRow(item)} field='DueDate' value={item.DueDate} resting={myWorkDate(item.DueDate)}
    restingFor={date => myWorkDate(date?.toISOString())} />,
  'Resolution Date': <TicketDateCell row={asRow(item)} field='ResolutionDate' value={item.ResolutionDate} resting={myWorkDate(item.ResolutionDate)}
    restingFor={date => myWorkDate(date?.toISOString())} />,
  'ResolutionCategory': <span>{item.ResolutionCategory ? item.ResolutionCategory : '-'}</span>,
  'Note': <span>{item.Note ? item.Note : '-'}</span>,
  'InternalNote': <span>{item.InternalNote ? item.InternalNote : '-'}</span>,
  'DraftKB': <span>{item.DraftKB ? <AcceptIcon />: EMPTY_CELL}</span>,
});

const getDateOrEmptyString = (date: string | null) => date ? date : '';

/** The app state, for the module-level cell renderers - set by DetailsListTable as it renders. */
const appStateForCells: { current: AppStateValue | null } = { current: null };

interface MyWorkEditFlags {
  tasksEditable: boolean;
  ticketsEditable: boolean;
  useTeam: boolean;
  useTicketType: boolean;
  useTicketLifecycle: boolean;
  resolutionNotesOn: boolean;
  resolutionNoteConfig?: TikitConfiguration;
  useTicketTags: boolean;
  useTicketTemplates: boolean;
  useTimeTracking: boolean;
  useGroupChat: boolean;
  useSLA: boolean;
  /** The licences have answered. Until then the list waits, or the licensed columns would arrive after the rows. */
  loaded: boolean;
}

/**
 * A row's cells, drawn once and handed back unchanged while the row and the app state stay the same, as the ticket
 * list does (ClassDetailList's cellCacheOf): dragging a column redraws every row, and React skips a cell whose element
 * is the one it drew last time. Rows are rebuilt as new objects whenever My Work's data changes; the editable cells
 * read MyWorkEditContext, which reaches them whether or not their element is reused.
 */
interface CellCacheEntry {
  state: AppStateValue | null;
  record?: { [x: string]: React.ReactElement };
  rest?: { [x: string]: React.ReactElement };
  cells: Map<string, React.ReactElement>;
}
const cellCache = new WeakMap<MyWorkListDataI, CellCacheEntry>();

const cellCacheOf = (item: MyWorkListDataI): CellCacheEntry => {
  const cached = cellCache.get(item);
  if (cached?.state === appStateForCells.current) return cached;
  const entry: CellCacheEntry = { state: appStateForCells.current, cells: new Map() };
  cellCache.set(item, entry);
  return entry;
};

const renderCPRecordValue =
  (customProperty: ICustomProperty) => (item: MyWorkListDataI) => {
    const { cells } = cellCacheOf(item);
    const key = `cp:${customProperty.Id}`;
    if (!cells.has(key)) cells.set(key, drawCPRecordValue(customProperty, item));
    return cells.get(key);
  };

const drawCPRecordValue = (customProperty: ICustomProperty, item: MyWorkListDataI) => {
    // Only tickets have custom properties - an empty answer would otherwise read as False on a True/False property.
    if (item.ItemType !== MyWorkListViewType.Tickets) return <span>{EMPTY_CELL}</span>;
    const answerValues = JSON.parse((item.CustomPropertiesAnswerJson?.length ?? 0) > 0 ? item.CustomPropertiesAnswerJson :  "{}");
    const resting = <CustomPropertyResting answers={answerValues} property={customProperty} />;
    return <TicketCustomPropertyCell row={asRow(item)} property={customProperty} answers={answerValues} resting={resting} />;
};

const renderRecordValue = (colName: string) => (item: MyWorkListDataI) => {
  if (item.Title === 'temp_ticket') return <SkeletonCell />;
  const entry = cellCacheOf(item);
  entry.record ??= renderRecord(item);
  const recorded = entry.record[colName];
  if (recorded != null) return recorded;
  entry.rest ??= renderRecordRest(item);
  return entry.rest[colName];
};

// name: [ key, name, fieldName, minWidth, maxWidth, isMultiline, isPadded, onRender ]
const _requestLength = (localStorage.getItem('TikitRequestLength') || 'abbreviated') == 'full';
const _cols: _ColsType = {
  Icon: [ListColumnKey.Icon, '', 'Icon', 10, 10, null, null, null],
  Id: [ListColumnKey.Id, t('ticket.ticket-list.id'), 'Id', 30, 30, null, true, renderRecordValue('Id')],
  Type: [ListColumnKey.Type, t('ticket.ticket-list.type'), 'TicketType', 100, 100, true, true, renderRecordValue('Type')],
  Title: [ListColumnKey.Title, t('ticket.ticket-list.title'), 'Title', (_requestLength ? 100 : 200), (_requestLength ? 200 : null), (_requestLength ? true : null), true, renderRecordValue('Title')],
  StatusName: [ListColumnKey.Status, t('ticket.ticket-list.status'), 'StatusName', 125, 125, null, null, renderRecordValue('StatusName')],
  GroupChat: [ListColumnKey.GroupChat, t('ticket.ticket-list.group-chat'), 'GroupChat', 100, 100, null, null, renderRecordValue('GroupChat')],
  Requester: [ListColumnKey.Requester, t('ticket.ticket-list.requester'), 'Requester', 150, 150, null, null, renderRecordValue('Requester')],
  Category: [ListColumnKey.Category, t('ticket.ticket-list.category'), 'Category', 125, 125, null, null, renderRecordValue('Category')],
  Priority: [ListColumnKey.Priority, t('ticket.ticket-list.priority'), 'Priority', 100, 100, null, null, renderRecordValue('Priority')],
  SLAs: [ListColumnKey.SLA, t('ticket.ticket-list.sla'), 'SLA', 100, 100, null, null, renderRecordValue('SLAs')],
  Team: [ListColumnKey.Team, t('ticket.ticket-list.team'), 'TeamName', 100, 100, null, null, renderRecordValue('Team')],
  Group: [ListColumnKey.Group, t('ticket.ticket-list.group'), 'GroupName', 100, 100, null, null, renderRecordValue('Group')],
  Assignee: [ListColumnKey.Assignee, t('ticket.ticket-list.assignee'), 'Assignee', 100, 100, null, true, renderRecordValue('Assignee')],
  // After Assignee, as the edit-ticket page lists them; hidden when Ticket Tags is unlicensed (sizedColumns).
  Tags: [ListColumnKey.Tags, t('ticket.add-ticket.tag'), 'Tags', 120, 200, null, null, renderRecordValue('Tags')],
  // Between Tags and Lifecycle, as on the ticket list; hidden when Ticket Templates is unlicensed (sizedColumns).
  Template: [ListColumnKey.Template, t('ticket-details.edit-ticket-view.template'), 'TemplateName', 100, 150, null, null, renderRecordValue('Template')],
  // 20% wider than most, as on the ticket list.
  Lifecycle: [ListColumnKey.Lifecycle, t('ticket.ticket-list.lifecycle'), 'LifecycleName', 120, 120, null, null, renderRecordValue('Lifecycle')],
  Phase: [ListColumnKey.Phase, t('ticket.ticket-list.phase'), 'LifecyclePhaseName', 120, 120, null, null, renderRecordValue('Phase')],
  // Then Total Agent Time, Source and the dates in the ticket list's order (Shaun, 2026-10-23); a task or approval shows
  // its ticket's time and source. Total Agent Time is hidden when Time Tracking is unlicensed (sizedColumns).
  TotalAgentTime: [ListColumnKey.TotalAgentTime, t('ticket.ticket-list.total-agent-time'), 'TotalTimeSpentMinutes', 120, 138, null, null, renderRecordValue('TotalAgentTime')],
  Source: [ListColumnKey.Source, t('automation.automation-properties.source'), 'SourceName', 100, 140, null, null, renderRecordValue('Source')],
  'Due Date': [ListColumnKey.DueDate, t('ticket.ticket-list.due-date'), 'DueDate', 100, 100, null, null, renderRecordValue('Due Date')],
  'Last Modified Date': [ListColumnKey.LastModifiedDate, t('ticket.ticket-list.last-modified-date'), 'LastModified', 100, 100, null, null, renderRecordValue('Last Modified Date')],
  'Created Date': [ListColumnKey.CreatedDate, t('ticket.ticket-list.created-date'), 'CreatedDate', 100, 100, null, null, renderRecordValue('Created Date')],
  'Resolution Date': [ListColumnKey.ResolutionDate, t('ticket.ticket-list.resolution-date'), 'ResolutionDate', 100, 100, null, null, renderRecordValue('Resolution Date')],
   ResolutionCategory : [ListColumnKey.ResolutionCategory, t('resolution-note.resolution-category'), 'ResolutionCategory', 100, 100, null, null, renderRecordValue('ResolutionCategory')],
   Note: [ListColumnKey.Note, t('resolution-note.resolution-note'), 'Note', (_requestLength ? 100 : 200), (_requestLength ? 100 : 200), (_requestLength ? true : null), true, renderRecordValue('Note')],
   InternalNote: [ListColumnKey.InternalNote, t('resolution-note.resolution-note-internal'), 'InternalNote', (_requestLength ? 100 : 200), (_requestLength ? 100 : 200), (_requestLength ? true : null), true, renderRecordValue('InternalNote')],
   DraftKB: [ListColumnKey.DraftKB, t('resolution-note.draft-kb'), 'DraftKB', 100, 100, null, null, renderRecordValue('DraftKB')]
};

const IconObj: {
  [key: string]: React.SVGAttributes<SVGElement>;
} = {
  [MyWorkListViewType.Tickets]: <TicketListIcon />,
  [MyWorkListViewType.Tasks]: <TaskListIcon width={16} height={16}/>,
  [MyWorkListViewType.Approvals]: <ApprovalListIcon />,
};

// Icon: the server accepts $orderby=Icon but returns the same rows in the same order either way (measured).
const NON_SORTABLE_COLUMNS = [ListColumnKey.Icon, ListColumnKey.SLA, ListColumnKey.Assignee, ListColumnKey.DraftKB, ListColumnKey.GroupChat];

/** Tags sorts on the Tickets and All tabs; tasks and approvals carry no tags (GetMyWork ignores it on those tabs). */
// The Tasks and Approvals tabs show only the columns their rows have (Shaun, 2026-10-23); the Tickets and All tabs show
// every column, a task or approval row reading "-" in the ones it lacks.
const TASK_COLUMNS = [
  ListColumnKey.Icon, ListColumnKey.Id, ListColumnKey.Title, ListColumnKey.Status, ListColumnKey.Team, ListColumnKey.Group,
  ListColumnKey.Assignee, ListColumnKey.Template, ListColumnKey.Lifecycle, ListColumnKey.Phase, ListColumnKey.LastModifiedDate,
  ListColumnKey.CreatedDate,
];
// Assignee stays: an approval row shows its approvers there.
const APPROVAL_COLUMNS = TASK_COLUMNS.filter(key => ![ListColumnKey.Team, ListColumnKey.Group].includes(key));
const TAB_COLUMNS: Partial<Record<number, Set<number>>> = {
  [MyWorkListViewType.Tasks]: new Set<number>(TASK_COLUMNS),
  [MyWorkListViewType.Approvals]: new Set<number>(APPROVAL_COLUMNS),
};

/** A row's key across tabs: a ticket, a task and an approval can share an Id. */
const myWorkRowKey = (row: { ItemType?: number; Id?: number }) => `${row.ItemType}-${row.Id}`;

const isSortableColumn = (columnKey: number, selectedTabId: number) =>
  !NON_SORTABLE_COLUMNS.some(x => Number(x) == columnKey) &&
  (columnKey !== ListColumnKey.Tags || selectedTabId === MyWorkListViewType.Tickets || selectedTabId === MyWorkListViewType.All);

const _onColumnClick = (
  onColumnClickCallback: (column: IColumn) => void,
  selectedTabId: number
) => (
  ev: React.MouseEvent<HTMLElement>,
  column: IColumn
): void => {
  if(!isSortableColumn(Number(column.key), selectedTabId)) return;

  onColumnClickCallback(column);
};

/**
 * The built-in columns, then the custom properties in their Settings > Custom Properties order (Shaun, 2026-10-23) -
 * built afresh each time rather than added to _cols, whose key order would keep whatever order they were first added in.
 */
const _colsWithProps = (currentState: AppStateValue, isCustomPropertiesEnabled: boolean) => {
    const customPropertyColId = 1000;
    const customPropertyCols = isCustomPropertiesEnabled
      ? [...(currentState?.customProperties ?? [])].filter(p => !p.IsHidden).sort((a, b) => a.Order - b.Order)
        .map(cp => [customPropertyColId + cp.Id, cp.Name, `${CustomPropertiesAnswerJsonKey}.${cp.PropertyId}`, 100, 100, null, null, renderCPRecordValue(cp)] as _ColsType[string])
      : [];
    return [...Object.values(_cols), ...customPropertyCols];
  }

const _columns = (
  myWorkStates: MyWorkStatesType,
  onColumnClickCallback: (column: IColumn) => void,
  isMyGroupWork: boolean,
  currentState: AppStateValue
): IColumn[] => {
  const selectedTabId = getSelectedTabFromStorage(isMyGroupWork) ?? myWorkStates.myWorkList.selectedTabId;
  
  const sortOrder = getMyWorkSortOrderFromStorage(isMyGroupWork, selectedTabId);
  const orderBy = sortOrder?.orderBy || myWorkStates.sortOrder?.[selectedTabId]?.orderBy;
  const orderType = sortOrder?.orderType || myWorkStates.sortOrder?.[selectedTabId]?.orderType;

  return markSortableColumns(_colsWithProps(currentState, myWorkStates.useCustomProperties).map((value): IColumn => {
    return {
      key: value[0] + '',
      name: value[1],
      fieldName: value[2],
      minWidth: value[3],
      maxWidth: value[4],
      isMultiline: value[5],
      isPadded: value[6],
      ...(value[7] && { onRender: value[7] }),
      isSorted: value[0] ===  orderBy ? true : null,
      isSortedDescending: value[0] === orderBy && orderType === EnumSortOrderType.desc ? true : false,
      onColumnClick: _onColumnClick(onColumnClickCallback, selectedTabId),
      isResizable: true,
      isCollapsible: true,
    };
  }), column => isSortableColumn(Number(column.key), selectedTabId));
};

const noDataFoundObj = {
  [MyWorkListViewType.All]: t('my-work.my-ticket.no-data-all'),
  [MyWorkListViewType.Tickets]: t('my-work.my-ticket.no-ticket'),
  [MyWorkListViewType.Tasks]: t('my-work.my-ticket.no-task'),
  [MyWorkListViewType.Approvals]: t('my-work.my-ticket.no-approval'),
}

const DetailsListTable = () => {
  const [selection, setSelection] = useState<Selection | undefined>();
  function _getSelectionDetails(): string {
    const selectionCount = selection ? selection.getSelectedCount() : 0;

    switch (selectionCount) {
      case 0:
        return 'No items selected';
      case 1:
        return (
          '1 item selected: ' +
          (selection.getSelection()[0] as IDetailsListBasicExampleItem).name
        );
      default:
        return `${selectionCount} items selected`;
    }
  }
  const currentstate = appState();
  const { myWorkStates, setMyWorkStates } = useContext(MyWorkContext);
  const { getpaginatedMyWorkList } = useMyWorkListFunctions(currentstate);
  const [state, setState] = useState({
    items: myWorkStates.myWorkList.allData,
    selectionDetails: _getSelectionDetails(),
    /** Rows a save in a cell is taking out of the list, by myWorkRowKey (rowLeave.ts). */
    leaving: new Map() as LeavingRows<string>,
  });
  const stateItemsRef = useRef<MyWorkListDataI[]>([]);
  stateItemsRef.current = state.items ?? [];
  /** Draws the rows as they now are, fading out the ones a save in a cell took out of the list (rowLeave.ts). */
  const [rowLeave] = useState(() => rowLeaver<MyWorkListDataI, string>(myWorkRowKey, {
    shown: () => stateItemsRef.current,
    update: next => setState(prev => ({ ...prev, ...next({ items: prev.items ?? [], leaving: prev.leaving }) })),
  }));
  const showRows = (rows: MyWorkListDataI[]) => rowLeave.show(rows);
  const leavingRef = useRef(state.leaving);
  leavingRef.current = state.leaving;
  // Reads the marks through a ref, so the grid is handed the same function on every render.
  const onRenderRow = useCallback<IRenderFunction<IDetailsRowProps>>((props, defaultRender) => {
    const phase = props && leavingRef.current.get(myWorkRowKey(props.item as MyWorkListDataI));
    return defaultRender(phase ? { ...props, className: `${props.className ?? ''} ${leaveClass(phase)}` } : props);
  }, []);
  const location = useLocation();

  const isMyGroupWork = location.pathname.includes(Route.myGroupWork.pagePath);

  const [columns, setColumns] = useState([] as IColumn[]);

  appStateForCells.current = currentstate;

  // Rows edit in place for a licensed analyst - task rows only with Ticket Tasks. The rest are what the ticket
  // list's cells need: Team where teams are licensed, Type where types are, the lifecycle rules, and the
  // resolution note settings (read as the ticket list reads them, TicketBoard).
  const [editFlags, setEditFlags] = useState<MyWorkEditFlags>({
    tasksEditable: false, ticketsEditable: false, useTeam: false, useTicketType: false, useTicketLifecycle: false,
    resolutionNotesOn: false, resolutionNoteConfig: undefined, useTicketTags: false,
    useTicketTemplates: false, useTimeTracking: false, useGroupChat: false, useSLA: false, loaded: false
  });
  useEffect(() => {
    Promise.all([
      getCachedFeature(PRODUCT_LICENSE.AnalystAccess),
      getCachedFeature(LICENSE_FEATURE.TicketTasks),
      getCachedFeature(LICENSE_FEATURE.MultiDepartment),
      getCachedFeature(LICENSE_FEATURE.TicketTypes),
      getCachedFeature(LICENSE_FEATURE.TicketLifecycle),
      getCachedFeature(LICENSE_FEATURE.ResolutionNote),
      getCachedFeature(LICENSE_FEATURE.TicketTags),
      getCachedFeature(LICENSE_FEATURE.TicketTemplates),
      new platformService().getSystemSetting(ConfigurationName.Resolution)
        .then(response => ({ ...response.data, IsResolutionNoteEnabled: response.data.IsEnabled }) as TikitConfiguration)
        .catch(() => undefined),
      getCachedFeature(LICENSE_FEATURE.TimeTracking),
      // Licensed and switched on in Settings - the server's GroupChatEnabled answers both.
      getCachedFeature(LICENSE_FEATURE.GroupChat),
      getCachedFeature(LICENSE_FEATURE.ServiceLevelAgreements)
    ]).then(([analyst, tasks, teams, types, lifecycles, notes, tags, templates, notesConfig, timeTracking, groupChat, slas]) => setEditFlags({
      tasksEditable: analyst && tasks && cellEditingOn(),
      ticketsEditable: analyst && cellEditingOn(),
      useTeam: teams,
      useTicketType: types,
      useTicketLifecycle: lifecycles,
      resolutionNotesOn: notes && !!notesConfig?.IsResolutionNoteEnabled,
      resolutionNoteConfig: notesConfig,
      useTicketTags: tags,
      useTicketTemplates: templates,
      useTimeTracking: timeTracking,
      useGroupChat: groupChat,
      useSLA: slas,
      loaded: true
    })).catch(error => {
      console.error('Error reading the licences for My Work', error);
      setEditFlags(flags => ({ ...flags, loaded: true }));
    });
  }, []);

  const onColumnClickCallback = (column: IColumn) => {
    const sortOrder = {
      orderBy: Number(column.key) as ISortOrderBy,
      orderType: column.isSortedDescending ? EnumSortOrderType.asc : EnumSortOrderType.desc,
    } as ISortOrder;
    
    const selectedTabId = getSelectedTabFromStorage(isMyGroupWork) ?? myWorkStates.myWorkList.selectedTabId;
    
    setMyWorkSortOrderInStorage(isMyGroupWork, selectedTabId, sortOrder);
    setMyWorkStates((prev) => ({
      ...prev,
      sortOrder: {
        ...prev.sortOrder,
        [selectedTabId]: sortOrder,
      },
      myWorkList: {
        ...prev.myWorkList,
        currentPage: 1,
        isRefreshing: true
      }
    }));

    getpaginatedMyWorkList({ sortOrder, page: 1, selectedTabNumber: selectedTabId, hideLoading: true });
  }

  useEffect(() => {
    const _selection: Selection = new Selection({
      onSelectionChanged: () =>
        setState((prev) => {
          return { ...prev, selectionDetails: _getSelectionDetails() };
        }),
    });
    setSelection(_selection);

    setColumns(_columns(myWorkStates, onColumnClickCallback, isMyGroupWork, currentstate));
  }, []);

  // The arrows follow the sort without re-mounting the list: Reset View fetches again with the rows left on
  // screen, so nothing else rebuilds the columns built above.
  useEffect(() => {
    const selectedTabId = getSelectedTabFromStorage(isMyGroupWork) ?? myWorkStates.myWorkList.selectedTabId;
    const sort = getMyWorkSortOrderFromStorage(isMyGroupWork, selectedTabId) ?? myWorkStates.sortOrder?.[selectedTabId];
    setColumns(prev => prev.map(column => {
      const isSorted = sort != null && Number(column.key) === Number(sort.orderBy);
      return { ...column, isSorted: isSorted ? true : null, isSortedDescending: isSorted && sort.orderType === EnumSortOrderType.desc };
    }));
  }, [myWorkStates.sortOrder]);

  useEffect(() => {
    const myWorkListdata = [...(myWorkStates.myWorkList.allData || [])];
    const workList = myWorkListdata.map((item) => {
      const requesters = [item.Requester, ...item.AffectedPlatformUsers];
      const assignee = [...item.AssigneeUsers, ...item.TicketCollaborators];
      
      return {
        ...item,
        Icon: IconObj[item.ItemType],
        StatusName: item.ItemType == MyWorkListViewType.Tickets ? hierarchialStatusName({ statusId: item.StatusId, ticketStatuses: withArchived(currentstate.ticketStatus, currentstate.archivedTicketStatus) }) : item.StatusName,
        StatusRecord: item.ItemType == MyWorkListViewType.Tickets && item.StatusId != null ? getStatus(item.StatusId, withArchived(currentstate.ticketStatus, currentstate.archivedTicketStatus)) : undefined,
        GroupChat: item.GroupChat,
        Requester: requesters.filter(x => x),
        Assignee: assignee.filter(x => x), 
        TeamName: item['TeamId'] ? item.TeamName : '-',
        GroupName: item['SupportGroupId'] ? item.SupportGroupName : '-',
        TicketType: item.ItemType === MyWorkListViewType.Approvals ? t('ticket-details.edit-ticket-form.approval.approval') :
                    item.ItemType === MyWorkListViewType.Tasks     ? t('ticket-details.edit-ticket-form.task.task') : 
                    item.TypeId                                    ? hierarchialTicketType({ ticketTypeId: item.TypeId, ticketTypes: withArchived(currentstate.ticketTypes, currentstate.archivedTicketTypes) }) : item.TypeName ?? '-',
        Category: item.CategoryId ? hierarchialCategoryName({ categoryId: item.CategoryId, categories: withArchived(currentstate.categories, currentstate.archivedCategories) }) : '-',
        Priority: item.PriorityId ? hierarchialPriorityName({ priorityId: item.PriorityId, priorities: withArchived(currentstate.priority, currentstate.archivedPriority) }) : '-',
        TicketLifecycleName: item.TicketLifecycle?.Title || '-',
        // What the Lifecycle column draws (its fieldName) - blank, unlike every other empty cell, without this.
        LifecycleName: item.LifecycleName || EMPTY_CELL,
        LifeCycleStatus: { Guid: item.LifecycleStatusId },
        Note: item.Note ?? '-',
        InternalNote: item.InternalNote ?? '-',
        ResolutionCategory: item.ResolutionCategory ?? '-',
        DraftKB: item.DraftKB
      };
    });
    showRows(workList);
  }, [myWorkStates.myWorkList]);

  useEffect(() => () => rowLeave.stop(), []);

  const getLayoutMode = () => {
    return (localStorage.getItem("TikitViewsHorizontalScroll") ?? "true") === "true"
      ? DetailsListLayoutMode.fixedColumns
      : DetailsListLayoutMode.justified;
  };

  const selectedTab = getSelectedTabFromStorage(isMyGroupWork) ?? myWorkStates.myWorkList.selectedTabId;
  const columnWidthsKey = myWorkColumnWidthsKey(isMyGroupWork, selectedTab);
  const columnOrderKey = myWorkColumnOrderKey(isMyGroupWork, selectedTab);
  const hiddenColumnsKey = myWorkHiddenColumnsKey(isMyGroupWork, selectedTab);
  const [columnOrderVersion, setColumnOrderVersion] = useState(0);
  const [layoutVersion, setLayoutVersion] = useState(0);

  // Reset View clears this tab's widths and order: rebuild, and re-mount the list so Fluent drops the
  // widths it was holding.
  useEffect(() => {
    const onChanged = (event: Event) => {
      const { storageKey, cleared } = (event as CustomEvent<ListPreferencesChangedDetail>).detail;
      if (!cleared || ![columnWidthsKey, columnOrderKey, hiddenColumnsKey].includes(storageKey)) return;
      setColumnOrderVersion(version => version + 1);
      setLayoutVersion(version => version + 1);
    };
    window.addEventListener(LIST_PREFERENCES_CHANGED, onChanged);
    return () => window.removeEventListener(LIST_PREFERENCES_CHANGED, onChanged);
  }, [columnWidthsKey, columnOrderKey, hiddenColumnsKey]);

  // Only the fixed-columns layout takes a starting width; see ClassDetailList.withSavedLayout. In auto-fit, as on
  // the ticket list, every column can be dragged down to the floor, and its default width is seeded below
  // (onDetailsListRef) so lowering the floor changes nothing until it is dragged.
  const sizedColumns = useMemo(() => {
    // The columns the ticket list hides while their feature is off (ClassDetailList.filterColumnsByFeature).
    const switchedOff = new Set<number>([
      ...(editFlags.useTicketType ? [] : [ListColumnKey.Type]),
      ...(editFlags.useGroupChat ? [] : [ListColumnKey.GroupChat]),
      ...(editFlags.useSLA ? [] : [ListColumnKey.SLA]),
      ...(editFlags.useTeam ? [] : [ListColumnKey.Team]),
      ...(editFlags.useTicketTags ? [] : [ListColumnKey.Tags]),
      ...(editFlags.useTicketTemplates ? [] : [ListColumnKey.Template]),
      ...(editFlags.useTicketLifecycle ? [] : [ListColumnKey.Lifecycle, ListColumnKey.Phase]),
      ...(editFlags.useTimeTracking ? [] : [ListColumnKey.TotalAgentTime]),
      ...(editFlags.resolutionNotesOn ? [] : [ListColumnKey.ResolutionCategory, ListColumnKey.Note, ListColumnKey.InternalNote, ListColumnKey.DraftKB]),
    ]);
    const onlyFor = TAB_COLUMNS[selectedTab];
    const licensed = columns.filter(column =>
      !switchedOff.has(Number(column.key)) && (!onlyFor || onlyFor.has(Number(column.key))));
    const shown = applyHiddenColumns(licensed, readHiddenColumns(hiddenColumnsKey));
    const ordered = applyColumnOrder(shown, readColumnOrder(columnOrderKey));
    return getLayoutMode() === DetailsListLayoutMode.fixedColumns
      ? applyColumnWidths(ordered, readColumnWidths(columnWidthsKey))
      : ordered.map(column => ({ ...column, minWidth: Math.min(column.minWidth, COLUMN_DRAG_FLOOR) }));
  }, [columns, columnWidthsKey, columnOrderKey, hiddenColumnsKey, columnOrderVersion, editFlags, selectedTab]);
  const sizedColumnsRef = useRef(sizedColumns);
  sizedColumnsRef.current = sizedColumns;

  // Only a real change is kept; see ClassDetailList._onColumnResize.
  const onColumnResize = (column: IColumn, newWidth: number) => {
    if (column.calculatedWidth != null && Math.round(column.calculatedWidth) === Math.round(newWidth)) return;
    saveColumnWidth(columnWidthsKey, column.key, newWidth);
  };

  // Saved widths in auto-fit; see ClassDetailList._onDetailsListRef. Kept stable: Fluent calls a changed
  // componentRef again, and every call re-renders, so a new function each render would loop.
  const onDetailsListRef = useCallback((detailsList: unknown) => {
    if (!detailsList || getLayoutMode() !== DetailsListLayoutMode.justified) return;
    // Saved widths, and every other column's default - their minWidth is now the floor (sizedColumns).
    if (seedAutoFitWidths(detailsList, sizedColumnsRef.current, readColumnWidths(columnWidthsKey))) setColumnOrderVersion(version => version + 1);
  }, [columnWidthsKey]);

  const columnReorderOptions = useMemo(() => ({
    onColumnDrop: ({ draggedIndex, targetIndex }: { draggedIndex: number; targetIndex: number }) => {
      writeListPreference(columnOrderKey, moveColumn(sizedColumns, draggedIndex, targetIndex).map(column => column.key));
      setColumnOrderVersion(version => version + 1);
    },
  }), [columnOrderKey, sizedColumns]);

  // The X on a header: the column is hidden on this tab until Reset View. A tab sorted by the removed column goes
  // back to the default sort, fetched again with the rows left on screen, as Reset View does (MyWorkToolbar).
  const onRemoveColumn = (columnKey: string) => {
    hideColumn(hiddenColumnsKey, columnKey);
    setColumnOrderVersion(version => version + 1);
    const sort = getMyWorkSortOrderFromStorage(isMyGroupWork, selectedTab) ?? myWorkStates.sortOrder?.[selectedTab];
    if (Number(sort?.orderBy) !== Number(columnKey)) return;
    setMyWorkSortOrderInStorage(isMyGroupWork, selectedTab, null);
    setMyWorkStates(prev => ({
      ...prev,
      sortOrder: { ...prev.sortOrder, [selectedTab]: DEFAULT_SORT_ORDER_MY_WORK },
      myWorkList: { ...prev.myWorkList, currentPage: 1, isRefreshing: true },
    }));
    getpaginatedMyWorkList({ sortOrder: DEFAULT_SORT_ORDER_MY_WORK, page: 1, selectedTabNumber: selectedTab, hideLoading: true });
  };

  const columnDragHandlers = useMemo(() => columnPointerDrag((draggedIndex, targetIndex) =>
    columnReorderOptions.onColumnDrop({ draggedIndex, targetIndex })), [columnReorderOptions]);

  // Read through a ref, so the context below can stay the same object while the page and tab it reloads change.
  const refreshRef = useRef<() => Promise<void>>();
  refreshRef.current = () => rowLeave.track(() =>
    // Quietly, as the ticket list reloads after a cell save: isRefreshing greys and freezes the whole table, which
    // is right for the toolbar's Refresh but made one cell's save look like a reload of the page.
    getpaginatedMyWorkList({ page: myWorkStates.myWorkList.currentPage, selectedTabNumber: selectedTab, hideLoading: true, afterSave: true }));

  // One object while the app state and licences stay the same: a new one on every render of this table - a row
  // selected, a tab counted - redrew all ~1,600 editable cells that read it, over a second on 90 rows.
  const editContext: MyWorkEditContextValue = useMemo(() => ({
    currentState: currentstate,
    ...editFlags,
    // A saved value put into its stored row at once - My Work and My Group Work rebuild the rows on screen from
    // allData - so a panel cell draws it and goes green without waiting for the reload (see ClassDetailList._patchRow).
    patch: (row, fields) => setMyWorkStates(prev => ({
      ...prev,
      myWorkList: {
        ...prev.myWorkList,
        allData: (prev.myWorkList.allData ?? []).map(item =>
          item.ItemType === row.ItemType && item.Id === row.Id ? { ...item, ...fields } : item)
      }
    })),
    refresh: () => refreshRef.current(),
  }), [currentstate, editFlags, setMyWorkStates]);

  if((myWorkStates.myWorkList?.allData?.length ?? 0) <= 0){
    return <EmptyData
      headerText={noDataFoundObj[getSelectedTabFromStorage(isMyGroupWork) || myWorkStates.myWorkList.selectedTabId]}
      SVGIcon={<EmptySVG height={200} width={200} />}
    />
  }

  // The rows are built a render after the data arrives (showRows, in an effect), and the selection a render after mount:
  // until both, the list's shimmer stays rather than an empty grid - it showed for ~0.1s between the shimmer and the
  // rows on opening My Work (Shaun, 2026-10-19), where the ticket list goes straight from one to the other.
  const rowsPending = !selection || !editFlags.loaded || ((myWorkStates.myWorkList.allData?.length ?? 0) > 0 && state.items.length === 0);
  return !rowsPending ? (
    <FluentUIThemeConsumer
      render={(globalTheme) => (
        <div
          className={`py-3.5 px-2.5 min-h-32 main-ticket-list frozen-list-header`}
          style={{
            backgroundColor: 'var(--mgt-theme-background2)',
            ...ThemeColorScheme(globalTheme.siteVariables),
          }}
        >
          <MyWorkEditContext.Provider value={editContext}>
          <div {...columnDragHandlers}>
            <MarqueeSelection selection={selection}>
              <DetailsList
                selectionZoneProps={NO_SELECT_ON_FOCUS}
                // Fluent's laid-out widths outrank the columns passed in, so each tab's saved
                // widths only apply on a fresh list.
                key={`${columnWidthsKey}#${layoutVersion}`}
                onColumnResize={onColumnResize}
                componentRef={onDetailsListRef}
                columnReorderOptions={columnReorderOptions}
                onRenderDetailsHeader={headerKeyedByColumnOrder(sizedColumns, {
                  // A header click flips the column's current direction, so it is handed the opposite of the one wanted.
                  onSortColumn: (column, descending) => onColumnClickCallback({ ...column, isSortedDescending: !descending }),
                  onRemoveColumn,
                })}
                items={state.items}
                onRenderRow={onRenderRow}
                compact={false}
                columns={sizedColumns}
                selectionMode={SelectionMode.none}
                // By row, as the ticket list's: a row's cells keep their state with it when a row above leaves.
                getKey={myWorkRowKey}
                setKey='multiple'
                layoutMode={getLayoutMode()}
                isHeaderVisible={true}
                selection={selection}
                selectionPreservedOnEmptyClick={true}
                enterModalSelectionOnTouch={true}
                ariaLabelForSelectionColumn='Toggle selection'
                ariaLabelForSelectAllCheckbox='Toggle selection for all items'
                checkButtonAriaLabel='select row'
                className="details-list-table"
              />
            </MarqueeSelection>
          </div>
          </MyWorkEditContext.Provider>
        </div>
      )}
    />
  ) : (
    <SkeletonTable />
  );
};

export default DetailsListTable;

type ColValuesType = [
  number,
  string,
  string,
  number,
  number,
  boolean | null,
  boolean | null,
  (item: MyWorkListDataI) => JSX.Element
];
type _ColsType = {
  [key: string]: ColValuesType;
};
